// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * F12.A: "Request abandonment" page — step 1 of the lost-passcode-AND-
 * lost-seed recovery flow. Users land here from the home page's "Lost
 * access to your account?" link. Submitting the form POSTs
 * `/auth/abandon-request` which emails a magic link. The response is
 * always `{ status: 'ok' }` regardless of whether the account exists,
 * so the UI can't leak account existence either.
 *
 * This page is intentionally scary-looking. Abandonment is irreversible
 * and users should ONLY be here if they've lost both factors. The copy
 * deliberately nudges them toward the passcode-reset flow (seed-only
 * loss) as the first option.
 */

import { useState } from 'preact/hooks';
import { ApiError, api } from '../api.js';
import '../styles/account-abandon.css';

export function AccountAbandon() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>(
    'idle',
  );
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('sending');
    setError(null);
    try {
      await api.abandonRequest({ email: email.trim() });
      setStatus('sent');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setError(
            "Too many requests. Wait a while before trying again — check your inbox in case you've already been sent a link.",
          );
        } else if (err.status === 400) {
          setError('Please enter a valid email address.');
        } else {
          setError('Something went wrong. Please try again in a moment.');
        }
      } else {
        setError('Something went wrong. Please try again in a moment.');
      }
      setStatus('error');
    }
  };

  return (
    <div class="abandon-page">
      <h1 class="abandon-title">Lost access to your account</h1>

      {status !== 'sent' && (
        <>
          <div class="abandon-warning">
            <p>
              <strong>Abandonment is irreversible.</strong> Use it only if you
              have lost <em>both</em> your passcode and your seed phrase.
            </p>
            <p>
              If you still have your seed phrase, you can{' '}
              <a href="/">reset your passcode</a> from the sign-in page without
              abandoning your account. That path is reversible and preserves
              the account history.
            </p>
          </div>

          <h2 class="abandon-subtitle">What happens when you abandon</h2>
          <p>
            Your Cobault account is ended. The Bitcoin held by each of your
            vaults is returned to the on-chain payout address you committed
            when the vault was created:
          </p>
          <ul class="abandon-bullets">
            <li>
              Vaults that never had an active trade are refunded within hours.
            </li>
            <li>
              Vaults with an active trade settle on their contract's expiry
              date (typically within days) and the settlement proceeds flow to
              your payout address automatically.
            </li>
            <li>
              You cannot cancel abandonment once confirmed; you cannot sign in
              to the abandoned account again.
            </li>
          </ul>

          <h2 class="abandon-subtitle">How it works</h2>
          <p>
            Enter your email below. We'll send a magic link that confirms
            abandonment when you click it — so an attacker who gains email
            access but doesn't know the rest can't silently abandon your
            account.
          </p>

          <form class="abandon-form" onSubmit={onSubmit}>
            <label class="abandon-label" for="abandon-email">
              Email address
            </label>
            <input
              id="abandon-email"
              type="email"
              class="abandon-input"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              autocomplete="email"
              autoFocus
              required
              disabled={status === 'sending'}
            />
            {error && <p class="abandon-error">{error}</p>}
            <div class="abandon-actions">
              <a class="abandon-cancel" href="/">
                Back to sign in
              </a>
              <button
                type="submit"
                class="abandon-submit"
                disabled={status === 'sending' || email.trim().length === 0}
              >
                {status === 'sending' ? 'Sending...' : 'Send recovery link'}
              </button>
            </div>
          </form>
        </>
      )}

      {status === 'sent' && (
        <div class="abandon-sent">
          <h2 class="abandon-subtitle">Check your inbox</h2>
          <p>
            If an account exists for <strong>{email}</strong>, we've sent a
            magic link. The link expires in about 15 minutes and can be used
            only once.
          </p>
          <p>
            Don't see it? Check spam; otherwise try again after 15 minutes. We
            deliberately don't confirm whether an email has an account, so a
            typo looks identical to "no link received."
          </p>
          <p>
            <a href="/">Back to sign in</a>
          </p>
        </div>
      )}
    </div>
  );
}
